import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Loader2, TrendingUp, TrendingDown, Minus, BookOpen, ClipboardCheck } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ParentSummaryCard = ({ studentId }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!studentId) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API}/parent/child-summary`, {
          params: { child_student_id: studentId },
        });
        if (!cancelled) setData(res.data);
      } catch (e) {
        console.error('parent summary load error', e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [studentId]);

  if (loading) {
    return (
      <div className="bg-white rounded-xl shadow-sm p-8 flex items-center justify-center" data-testid="parent-summary-loading">
        <Loader2 className="animate-spin text-green-500" size={22} />
      </div>
    );
  }

  if (!data) return null;

  const improvement = data.improvement_pct || 0;
  const positive = improvement > 0;
  const flat = improvement === 0;
  const TrendIcon = positive ? TrendingUp : flat ? Minus : TrendingDown;
  const trendClass = positive ? 'text-emerald-600 bg-emerald-50' : flat ? 'text-gray-600 bg-gray-100' : 'text-red-600 bg-red-50';

  return (
    <div className="bg-white rounded-xl shadow-sm overflow-hidden" data-testid="parent-summary-card">
      <div className="bg-gradient-to-r from-green-500 to-teal-500 px-6 py-4 text-white">
        <h3 className="font-semibold text-lg">Parent Snapshot</h3>
        <p className="text-green-100 text-xs">Progress at a glance for {data.student?.name}</p>
      </div>
      <div className="p-4 grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className={`rounded-lg p-4 ${trendClass}`} data-testid="parent-improvement-tile">
          <div className="flex items-center justify-between mb-1">
            <span className="text-xs uppercase tracking-wide opacity-80">Improvement</span>
            <TrendIcon size={18} />
          </div>
          <p className="text-2xl font-bold">
            {improvement > 0 ? '+' : ''}{improvement}%
          </p>
          <p className="text-[11px] opacity-75 mt-1">
            {data.recent_avg}% recent vs {data.older_avg}% earlier
          </p>
        </div>
        <div className="rounded-lg p-4 bg-blue-50 text-blue-700" data-testid="parent-tests-tile">
          <div className="flex items-center justify-between mb-1">
            <span className="text-xs uppercase tracking-wide opacity-80">Tests Taken</span>
            <BookOpen size={18} />
          </div>
          <p className="text-2xl font-bold">{data.tests_attempted}</p>
          <p className="text-[11px] opacity-75 mt-1">Total attempts</p>
        </div>
        <div className="rounded-lg p-4 bg-purple-50 text-purple-700" data-testid="parent-homework-tile">
          <div className="flex items-center justify-between mb-1">
            <span className="text-xs uppercase tracking-wide opacity-80">Homework</span>
            <ClipboardCheck size={18} />
          </div>
          <p className="text-2xl font-bold">{data.homework_completion_pct || 0}%</p>
          <p className="text-[11px] opacity-75 mt-1">
            {data.homework_completed}/{data.homework_assigned} completed
          </p>
        </div>
        <div className="rounded-lg p-4 bg-amber-50 text-amber-700" data-testid="parent-class-tile">
          <div className="flex items-center justify-between mb-1">
            <span className="text-xs uppercase tracking-wide opacity-80">Class</span>
          </div>
          <p className="text-2xl font-bold">{data.student?.class_level || '-'}{data.student?.section ? ` · ${data.student.section}` : ''}</p>
          <p className="text-[11px] opacity-75 mt-1">Section</p>
        </div>
      </div>
    </div>
  );
};

export default ParentSummaryCard;
